import { useTranslation } from 'react-i18next';

import { Badge } from '@/components/atoms/Badge';
import { Button } from '@/components/atoms/Buttons';
import { Spinner } from '@/components/atoms/Spinner';
import EmptyState from '@/components/molecules/EmptyState';
import { ModalComponent } from '@/components/molecules/ModalComponent';
import { DataTable, type DataTableColumnDef } from '@/components/organisms/DataTable';
import SomethingHappened from '@/components/SomethingHappened';
import { type CageFishGroupHistoryItem, useGetCageFishGroupHistory } from '@/domain/fishGroups';
import { extractDateFromISO, formatDateByPattern } from '@/utils/date';

import FishGroupStatusBadge from '../FishGroupStatusBadge';

import styles from './CageTimelineModal.module.scss';

interface Props {
    clientId: number;
    cage: { id: number; name: string } | null;
    isOpen: boolean;
    onClose: () => void;
    /**
     * Triggered when the operator clicks a fish group name in the list. The
     * parent is expected to close this modal and open the fish-group detail
     * modal, so the operator drills naturally from "what happened in the cage"
     * into "what happened with this fish group".
     */
    onOpenFishGroup: (fishGroupId: number) => void;
}

const PLACEHOLDER = '—';
const ROW_HEIGHT = 44;
const MS_PER_DAY = 24 * 60 * 60 * 1000;

export default function CageTimelineModal(props: Props) {
    const { clientId, cage, isOpen, onClose, onOpenFishGroup } = props;
    const { t } = useTranslation('fishGroups');

    const { data, isLoading, error } = useGetCageFishGroupHistory(clientId, cage?.id ?? null);

    const body = renderBody({ isLoading, error: error ?? null, data, onOpenFishGroup });

    return (
        <ModalComponent
            isOpen={isOpen}
            onClose={onClose}
            title={t('cageTimeline.title', { cageName: cage?.name ?? '' })}
            body={body}
            size="lg"
        />
    );
}

interface RenderBodyArgs {
    isLoading: boolean;
    error: unknown;
    data: CageFishGroupHistoryItem[];
    onOpenFishGroup: (fishGroupId: number) => void;
}

function renderBody(args: RenderBodyArgs) {
    const { isLoading, error, data, onOpenFishGroup } = args;
    if (isLoading) return <Spinner size="lg" />;
    if (error) return <SomethingHappened />;
    return <CageHistoryList items={data} onOpenFishGroup={onOpenFishGroup} />;
}

interface CageHistoryListProps {
    items: CageFishGroupHistoryItem[];
    onOpenFishGroup: (fishGroupId: number) => void;
}

function CageHistoryList(props: CageHistoryListProps) {
    const { items, onOpenFishGroup } = props;
    const { t, i18n } = useTranslation('fishGroups');

    if (items.length === 0) {
        return <EmptyState title={t('cageTimeline.empty')} appearance="quiet" />;
    }

    const now = new Date();
    const formatDay = (iso: string) =>
        formatDateByPattern(extractDateFromISO(iso), 'd MMM yyyy', i18n.language);

    const columns: DataTableColumnDef<CageFishGroupHistoryItem>[] = [
        {
            id: 'fishGroup',
            header: t('cageTimeline.col.fishGroup'),
            meta: { width: '1.5fr' },
            cell: ({ row }) => (
                <Button
                    variant="link"
                    type="button"
                    onClick={() => onOpenFishGroup(row.original.fishGroupId)}>
                    {row.original.fishGroupName}
                </Button>
            ),
        },
        {
            id: 'status',
            header: t('cageTimeline.col.status'),
            meta: { width: '0.9fr' },
            cell: ({ row }) => <FishGroupStatusBadge status={row.original.status} />,
        },
        {
            id: 'from',
            header: t('cageTimeline.col.from'),
            cell: ({ row }) => formatDay(row.original.fromDate),
        },
        {
            id: 'to',
            header: t('cageTimeline.col.to'),
            cell: ({ row }) =>
                row.original.toDate === null ? (
                    <Badge tone="success" shape="tag">
                        {t('cageTimeline.current')}
                    </Badge>
                ) : (
                    formatDay(row.original.toDate)
                ),
        },
        {
            id: 'duration',
            header: t('cageTimeline.col.duration'),
            meta: { width: '0.9fr' },
            cell: ({ row }) =>
                t('cageTimeline.durationDays', { count: computeDurationDays(row.original, now) }),
        },
        {
            id: 'reason',
            header: t('cageTimeline.col.reason'),
            meta: { width: '1.2fr' },
            cell: ({ row }) =>
                row.original.reason ?? <span className={styles.placeholder}>{PLACEHOLDER}</span>,
        },
    ];

    return (
        <DataTable
            data={items}
            columns={columns}
            getRowId={(item) => `${item.fishGroupId}-${item.fromDate}`}
            layout="grid"
            enableSorting={false}
            rowHeight={ROW_HEIGHT}
            getRowProps={() => ({ disableHover: true })}
        />
    );
}

/**
 * Days the fish group occupied the cage. For ongoing occupations we measure
 * up to `now` so the duration keeps growing as long as the FG stays. Rounded
 * up so a same-day move shows 1 day instead of 0.
 */
function computeDurationDays(item: CageFishGroupHistoryItem, now: Date): number {
    const fromMs = Date.parse(item.fromDate);
    const toMs = item.toDate ? Date.parse(item.toDate) : now.getTime();
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs) || toMs < fromMs) return 0;
    return Math.max(1, Math.ceil((toMs - fromMs) / MS_PER_DAY));
}
